import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import ExcelButton from '../../../../components/common/table/excel-button/excel-button';
import Table from '../../../../components/common/table/table';
import FertilizationPlan from '../../../../interfaces/fertilization-plan';
import Field from '../../../../interfaces/field';
import {
  CROP_FERTILIZATION_PLAN_CREATE,
  CROP_FERTILIZATION_PLAN_VIEW,
} from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { can, route } from '../../../../services/utils';
import { RootState } from '../../../../store/store';
import css from './index.module.css';

export interface FertilizationPlanTable extends Field {
  fertilization_plan: FertilizationPlan;
  'fertilization_plan.needed_n': number;
  'fertilization_plan.needed_p2o5': number;
  'fertilization_plan.needed_k2o': number;
  'fertilization_plan.balance_n': number;
  'fertilization_plan.balance_p2o5': number;
  'fertilization_plan.balance_k2o': number;
  crop_change_plan: object; //CropChangePlan;
  'crop_change_plan.breed': string;
  production_type_crop: string;
}

export default function FertilizationPlanTableIndex() {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.fertilization-plan.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const { farmId, season } = useParams<{
    farmId: string;
    season: string;
  }>();

  const { fertilizationPlanService } = useService();
  const [selectedRows, setSelectedRows] = useState([]);

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));
  // allow selecting only one row
  useEffect(() => {
    if (selectedRows.length > 1) {
      setSelectedRows(() => [selectedRows[selectedRows.length - 1]]);
    }
  }, [selectedRows]);

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) =>
      fertilizationPlanService.planIndex(farmId, season, {
        page,
        sort,
        desc,
        ...filter,
      }),
    [fertilizationPlanService, farmId, season]
  );

  const toolbar = useCallback(
    () => (
      <div className={css['topbar']}>
        <ExcelButton
          onClick={() =>
            fertilizationPlanService.exportXls(farmId, season, {}).subscribe()
          }
        />
      </div>
    ),
    [farmId, season, fertilizationPlanService]
  );

  return (
    <div className={css['body']}>
      <Table<FertilizationPlanTable>
        toolbar={toolbar}
        request={request}
        keyAccessor="id"
        columnGroups={[
          {
            header: tp('needed_fertilizer'),
            startsAtAccessor: 'fertilization_plan.needed_n',
            colspan: 3,
          },
          {
            header: tp('balance_fertilizer'),
            startsAtAccessor: 'fertilization_plan.balance_n',
            colspan: 3,
          },
        ]}
        columns={[
          {
            accessor: 'lad_number',
            header: tp('lad_number'),
            className: css['strong'],
          },
          {
            accessor: 'name',
            header: tp('name'),
            sortable: true,
            className: css['strong'],
          },
          {
            accessor: 'area',
            valuable: (value, field) => field.fertilization_plan?.area || value,
            header: tp('area'),
          },
          {
            accessor: 'foregrowth_crop_value',
            header: tp('foregrowth_crop'),
          },
          {
            accessor: 'production_type_crop',
            header: tp('crop'),
          },
          {
            accessor: 'crop_change_plan.breed',
            header: tp('breed'),
          },
          {
            accessor: 'crop_change_plan',
            valuable: (value) => {
              if (!value?.production_type_info) {
                return '';
              }
              const amount = value?.production_type_info[0]?.yield;
              const unit = value?.production_type_info[0]?.yield_unit;
              return amount && unit ? `${amount} ${unit}` : '';
            },
            header: tp('estimated_crop_yield'),
          },
          {
            accessor: 'fertilization_plan.needed_n',
            header: tp('n'),
            className: css['fertilizer-column'],
            headerClassName: css['fertilizer-header'],
          },
          {
            accessor: 'fertilization_plan.needed_p2o5',
            header: tp('p2o5'),
            className: css['fertilizer-column'],
            headerClassName: css['fertilizer-header'],
          },
          {
            accessor: 'fertilization_plan.needed_k2o',
            header: tp('k2o'),
            className: css['fertilizer-column'],
            headerClassName: css['fertilizer-header'],
          },
          {
            accessor: 'fertilization_plan.balance_n',
            header: tp('n'),
            className: css['fertilizer-column'],
            headerClassName: css['fertilizer-header'],
          },
          {
            accessor: 'fertilization_plan.balance_p2o5',
            header: tp('p2o5'),
            className: css['fertilizer-column'],
            headerClassName: css['fertilizer-header'],
          },
          {
            accessor: 'fertilization_plan.balance_k2o',
            header: tp('k2o'),
            className: css['fertilizer-column'],
            headerClassName: css['fertilizer-header'],
          },
        ]}
        viewRoute={(row) => {
          switch (true) {
            case row.fertilization_plan &&
              can(farmUser, { title: 'view fertilization_plan' }):
              return route(CROP_FERTILIZATION_PLAN_VIEW, {
                farmId: farmId,
                season: season,
                id: row.fertilization_plan.id,
              });
            case !row.fertilization_plan &&
              can(farmUser, { title: 'create fertilization_plan' }):
              return route(CROP_FERTILIZATION_PLAN_CREATE, {
                farmId: farmId,
                season: season,
                fieldId: row.id,
              });
            default:
              break;
          }
        }}
        viewRecordAbility={{ title: 'viewAny field' }}
        selectedRowsState={null}
      />
    </div>
  );
}
